import { Colors } from "@utils";
import React from "react";

import logo from "@assets/images/logo.png";
import styled from "styled-components";
import { GradeBGColor, GradeTrophyIcon, HealthAgeIcon } from "@utils/Constants";
import PartHealthGradeView from "./PartHealthGradeView";

interface ReportViewProps {
  analyzedData: any;
}

const Divider = styled.div`
  height: 1px;
  background: #ebebeb;
`;

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 9px 12px;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
`;

const TrophyContainer = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 30px;
  background: #e6faf6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GradeChip = styled.div`
  padding: 0px 8px;
  height: 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: bold;
  color: ${Colors.white};
`;

const HealthAgeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 12px;
  row-gap: 10px;
`;

const UNKNOWN_COLOR = Colors.gray2;

const ReportView = function (props: ReportViewProps) {
  const { analyzedData } = props;

  const getBGColor = (grade: HealthGrade) => {
    return GradeBGColor[grade] ?? UNKNOWN_COLOR;
  };

  const isNumber = (value: any) => {
    return typeof value === "number" && !Number.isNaN(value);
  };

  const ageDiff = React.useMemo(() => {
    const realAge = analyzedData["나이"];
    const healthAge = analyzedData["건강나이"];

    if (isNumber(realAge) && isNumber(healthAge)) {
      const diff = Math.abs(realAge - healthAge);

      if (realAge === healthAge) {
        return {
          pre: "건강 나이는 검진 당시 나이와",
          diff: "같습니다",
          post: "",
        };
      } else if (realAge > healthAge) {
        return {
          pre: "건강 나이는 검진 당시 나이보다",
          diff: `${diff}살 어린 ${healthAge}살`,
          post: "입니다",
        };
      } else if (realAge < healthAge) {
        return {
          pre: "건강 나이는 검진 당시 나이보다",
          diff: `${diff}살 많은 ${healthAge}살`,
          post: "입니다",
        };
      }
    }

    return {
      pre: "-",
      diff: "",
      post: "",
    };
  }, [analyzedData]);

  return (
    <div className="flex flex-col">
      <img src={logo} alt="" className="w-[120px] my-[18px]" />
      <Divider className="mb-[10px]" />
      <div
        className="text-[16px] font-[600] mb-[6px]"
        style={{
          color: "#2A2B2C",
        }}
      >
        건강 종합 평가
      </div>
      <SummaryContainer>
        <TrophyContainer>
          <img
            className="w-[38px]"
            src={GradeTrophyIcon[analyzedData["종합등급"] as HealthGrade]}
            alt=""
          />
        </TrophyContainer>
        <div className="pl-[14px] flex flex-col justify-center">
          <GradeChip
            className="mb-[4px] self-start"
            style={{ background: getBGColor(analyzedData["종합등급"]) }}
          >
            {analyzedData["종합등급"]}
          </GradeChip>
          <div
            className="text-[15px]/[21px] font-[600] whitespace-pre-wrap"
            style={{
              color: "#626B72",
            }}
          >
            {ageDiff.pre}
            {"\n"}
            <span className="font-[700]" style={{ color: "#2A2B2C" }}>
              {ageDiff.diff}
            </span>
            {ageDiff.post}
          </div>
        </div>
      </SummaryContainer>
      <div
        className="text-[16px] font-[600] mt-[12px] mb-[6px]"
        style={{
          color: "#2A2B2C",
        }}
      >
        신체건강 성적
      </div>
      <HealthAgeGrid>
        {Object.keys(HealthAgeIcon).map((part) => (
          <PartHealthGradeView
            key={part}
            part={part as HealthPart}
            grade={analyzedData[`${part}등급`]}
          />
        ))}
      </HealthAgeGrid>
      <div
        className="text-[12px]/[17px] whitespace-pre-wrap mt-[24px]"
        style={{
          color: "#626B72",
        }}
      >
        *등급은 최우수, 우수, 보통, 미흡 총 4단계로 구성되어있습니다.{"\n"}
        건강성적표는 <b>서울대학교 보건환경 연구소</b>와 렉스소프트에서 개발한
        {"\n"}AI 건강 분석 서비스입니다.
      </div>
    </div>
  );
};

export default ReportView;
